import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { products } from './Products';
import { ProductType } from './Product';
import { useCart } from '../Cart/CartContext';
import styles from './ProductDetail.module.css';

const ProductDetail: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const [product, setProduct] = useState<ProductType | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoplay, setIsAutoplay] = useState(true);
  const [selectedSize, setSelectedSize] = useState<string | undefined>(undefined);
  const autoplayTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { addToCart } = useCart();

  useEffect(() => {
    if (productId) {
      const foundProduct = products.find(p => p.id === parseInt(productId));
      if (foundProduct) {
        setProduct(foundProduct);
        // Initialize size if it's an armband product
        if (foundProduct.category === "armband" && foundProduct.size && foundProduct.size.length > 0) {
          setSelectedSize(foundProduct.size[0]);
        }
      }
    }
  }, [productId]);

  useEffect(() => {
    // Clean up timer when component unmounts
    return () => {
      if (autoplayTimerRef.current) {
        clearInterval(autoplayTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!product || !isAutoplay) return;

    const images = getProductImages(product);

    if (images.length <= 1) return;

    // Start the automatic image switching timer
    autoplayTimerRef.current = setInterval(() => {
      setCurrentImageIndex(prevIndex => (prevIndex + 1) % images.length);
    }, 5000); // Switch every 5 seconds

    return () => {
      if (autoplayTimerRef.current) {
        clearInterval(autoplayTimerRef.current);
      }
    };
  }, [product, isAutoplay]);

  // Helper function to get all product images
  const getProductImages = (product: ProductType): string[] => {
    if (product.images && product.images.length > 0) {
      return product.images;
    }
    // If no images array is provided, use the main image as fallback
    return [product.image];
  };

  const goToNextImage = () => {
    if (!product) return;
    // Stop autoplay when navigation arrows are clicked
    setIsAutoplay(false);
    const images = getProductImages(product);
    setCurrentImageIndex(prevIndex => (prevIndex + 1) % images.length);
  };

  const goToPrevImage = () => {
    if (!product) return;
    // Stop autoplay when navigation arrows are clicked
    setIsAutoplay(false);
    const images = getProductImages(product);
    setCurrentImageIndex(prevIndex => (prevIndex - 1 + images.length) % images.length);
  };

  const handleAddToCart = () => {
    if (!product) return;
    
    // For armband products, require size selection
    if (product.category === "armband") {
      if (!selectedSize) {
        alert("Vennligst velg størrelse før du legger varen i handlekurven");
        return;
      }
      addToCart(product, selectedSize);
    } else {
      addToCart(product);
    }
  };

  if (!product) {
    return <div className="container mt-5">Produktet ble ikke funnet</div>;
  }

  const images = getProductImages(product);
  const currentImage = images[currentImageIndex].replace('_1x1', '_5x4');

  return (
    <section className="container mt-5">
      <Helmet>
        <title>{`${product.name} | Perler med Vilje`}</title>
        <meta name="description" content={product.description.replace(/\n/g, ' ').slice(0, 155)} />
        <link rel="canonical" href={`https://www.perlermedvilje.no/product/${product.id}`} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${product.name} | Perler med Vilje`} />
        <meta property="og:description" content={product.description.replace(/\n/g, ' ').slice(0, 200)} />
        <meta property="og:image" content={`https://www.perlermedvilje.no${currentImage}`} />
        <meta property="product:price:amount" content={product.price.toString()} />
        <meta property="product:price:currency" content="NOK" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: product.name,
          image: [`https://www.perlermedvilje.no${currentImage}`],
          description: product.description.replace(/\n/g, ' '),
          sku: product.id,
          offers: {
            '@type': 'Offer',
            priceCurrency: 'NOK',
            price: product.price,
            availability: product.inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
            url: `https://www.perlermedvilje.no/product/${product.id}`
          }
        })}</script>
      </Helmet>
      <div className="row">
        <div className="col-md-6">
          <div className={`img-container ${styles.imageContainer}`}>
            <img
              src={currentImage}
              alt={`${product.name} - bilde ${currentImageIndex + 1}`}
              className={`img-fluid ${styles.productImage}`}
            />
            
            {images.length > 1 && (
              <>
                <button 
                  className={styles.navButton} 
                  onClick={goToPrevImage} 
                  aria-label="Forrige bilde"
                  style={{ left: '10px' }}
                >
                  &lsaquo;
                </button>
                <button 
                  className={styles.navButton} 
                  onClick={goToNextImage} 
                  aria-label="Neste bilde"
                  style={{ right: '10px' }}
                >
                  &rsaquo;
                </button>
                <div className={styles.pagination}>
                  {currentImageIndex + 1}/{images.length}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <h2 className="mt-4 mt-md-0">{product.name}</h2>
          <div className={`lead ${styles.productDescription}`}>
            {product.description.split('\n\n').map((paragraph, index) => (
              <p key={index}>{paragraph.replace(/\n/g, ' ')}</p>
            ))}
          </div>
          <p className="fw-bold fs-4">NOK {product.price},-</p>
          
          {product.inStock ? (
            <p className="text-success">På lager</p>
          ) : (
            <p className="text-danger">Ikke på lager</p>
          )}
          
          {/* Size selection for armband products */}
          {product.category === "armband" && product.size && product.size.length > 0 && (
            <div className="mt-3 mb-3">
              <label className="mb-2 fw-bold">Størrelse:</label>
              <div className="d-flex">
                {product.size.map((size) => (
                  <button
                    key={size}
                    className={`btn ${selectedSize === size ? 'btn-dark' : 'btn-outline-dark'} me-2`}
                    onClick={() => setSelectedSize(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          <button 
            className="btn btn-dark btn-lg mt-3" 
            onClick={handleAddToCart}
            disabled={!product.inStock}
          >
            Legg i handlekurv
          </button>
          
          <div className="mt-4">
            <Link to={`/category/${product.category}`} className="btn btn-outline-secondary">
              Tilbake til {product.category}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductDetail;
